<template>
  <el-dialog
    :model-value="visible"
    :title="title"
    width="1100px"
    :close-on-click-modal="false"
    top="3vh"
    @update:model-value="$emit('update:visible', $event)"
    @open="initData"
  >
    <div class="media-picker">
      <!-- 左侧：目录树 -->
      <div class="picker-sidebar">
        <div class="sidebar-header">
          <span>素材目录</span>
          <el-button size="small" text style="padding:2px 4px" @click="showAddCategoryDialog">
            <el-icon size="14"><Plus /></el-icon>
          </el-button>
        </div>
        <!-- 全部素材（放在树最上方） -->
        <div class="all-media-item"
          :class="{ active: currentCategoryId === null }"
          @click="handleCategoryClick(null)">
          <el-icon size="12" style="margin-right:4px"><PictureFilled /></el-icon>
          <span>全部素材</span>
        </div>
        <el-tree
          ref="categoryTreeRef"
          :data="categoryTree"
          :props="{ label: 'name', children: 'children' }"
          node-key="id"
          :highlight-current="true"
          :expand-on-click-node="false"
          default-expand-all
          @node-click="handleCategoryClick"
        >
          <template #default="{ node, data }">
            <span class="picker-category-node">
              <el-icon size="12" style="color:#e6a23c;margin-right:4px"><FolderOpened /></el-icon>
              <span class="node-label">{{ node.label }}</span>
              <el-button size="small" text style="padding:0 3px" @click.stop="showAddChildDialog(data)">
                <el-icon size="12"><FolderAdd /></el-icon>
              </el-button>
            </span>
          </template>
        </el-tree>
      </div>

      <!-- 右侧主区域 -->
      <div class="picker-main">
        <!-- 顶部工具栏（单行） -->
        <div class="toolbar">
          <div class="toolbar-left">
            <el-upload
              ref="uploadRef"
              multiple
              :action="uploadAction"
              :headers="uploadHeaders"
              :data="{ category_id: uploadCategoryId }"
              :key="uploadCategoryId"
              :show-file-list="false"
              :on-success="handleUploadSuccess"
              :before-upload="beforeUpload"
              accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/mpeg,video/quicktime,video/webm"
            >
              <el-button size="small" type="primary" style="height:28px;font-size:12px">
                <el-icon size="14"><Upload /></el-icon> 上传
              </el-button>
            </el-upload>
          </div>
          <div class="toolbar-right">
            <el-select v-model="filterType" placeholder="类型" size="small" :clearable="!props.accept" :disabled="!!props.accept" style="width:90px" @change="fetchMedia">
              <el-option v-if="!props.accept" label="全部" value="" />
              <el-option label="图片" value="image" />
              <el-option label="视频" value="video" />
            </el-select>
            <el-input
              v-model="searchKeyword"
              placeholder="搜索..."
              clearable
              size="small"
              style="width:160px"
              @input="handleSearch"
            />
            <span class="toolbar-count">共 {{ pagination.total }} 条</span>
          </div>
        </div>

        <!-- 素材网格（行分割，每行 5 列） -->
        <div v-loading="loading" class="media-grid">
          <div v-for="(row, ri) in rows" :key="ri" class="media-row">
            <div
              v-for="item in row"
              :key="item.id"
              class="media-item"
              :class="{ selected: selectedIds.includes(item.id) }"
              @click="handleSelect(item)"
          >
            <!-- 图片 -->
            <div v-if="item.type === 'image'" class="media-preview">
              <img :src="item.url" class="media-preview-img" @error="onImgError($event)" />
            </div>
            <!-- 视频 -->
            <div v-else class="media-preview video-preview">
              <video :src="item.url" muted preload="metadata" />
              <div class="video-badge"><el-icon size="16"><VideoCameraFilled /></el-icon></div>
            </div>

            <!-- 底部信息条（半透明覆盖） -->
            <div class="media-bar" @click.stop>
              <span class="bar-name" :title="item.name">{{ item.name }}</span>
              <button class="bar-btn" title="预览" @click="handlePreview(item)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              </button>
              <button class="bar-btn" title="复制" @click="handleCopyUrl(item.url)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
            </div>

            <!-- 选中标记 -->
            <div v-if="selectedIds.includes(item.id)" class="selected-mark">
              <el-icon size="12"><Check /></el-icon>
            </div>
          </div>
          </div>

          <div v-if="!loading && mediaList.length === 0" class="empty-state">
            <el-empty :description="searchKeyword ? '无匹配结果' : '暂无素材'" :image-size="60" />
          </div>
        </div>

        <!-- 底部分页（极简） -->
        <div class="pagination-bar" v-if="pagination.total > 0">
          <el-pagination
            v-model:current-page="pagination.current_page"
            v-model:page-size="pagination.per_page"
            :page-sizes="[28, 56, 84]"
            :total="pagination.total"
            layout="prev, pager, next, sizes"
            small
            @change="fetchMedia"
          />
        </div>
      </div>
    </div>

    <!-- 新增目录弹窗 -->
    <el-dialog v-model="categoryDialogVisible" title="新建目录" width="380px" append-to-body>
      <el-form ref="categoryFormRef" :model="categoryForm" :rules="categoryRules">
        <el-form-item label="名称" prop="name">
          <el-input v-model="categoryForm.name" placeholder="目录名称" size="small" />
        </el-form-item>
        <el-form-item label="上级">
          <el-tree-select
            v-model="categoryForm.parent_id"
            :data="categoryTree"
            :props="{ label: 'name', children: 'children', value: 'id' }"
            placeholder="顶级目录"
            clearable check-strictly size="small" style="width:100%"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="categoryDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="handleCreateCategory" :loading="categorySubmitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- 大图预览 -->
    <el-dialog v-model="previewDialogVisible" :title="previewName" width="800px" top="5vh" append-to-body>
      <div class="preview-image-wrap">
        <el-image :src="previewUrl" fit="contain" style="width:100%;max-height:75vh" />
      </div>
    </el-dialog>

    <template #footer>
      <div class="dlg-footer">
        <span v-if="mode === 'multiple'" class="multi-count">已选 {{ selectedIds.length }}{{ maxCount ? ' / ' + maxCount : '' }}</span>
        <span v-else></span>
        <div class="dlg-actions">
          <el-button size="small" @click="$emit('update:visible', false)">取消</el-button>
          <el-button size="small" type="primary" @click="confirmSelect" :disabled="selectedIds.length === 0">确认</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import * as mediaApi from '../api/media'

const props = defineProps({
  visible: Boolean,
  mode: { type: String, default: 'single' },
  title: { type: String, default: '选择素材' },
  maxCount: { type: Number, default: 0 },
  modelValue: null,
  accept: { type: String, default: '' }, // 'image' | 'video' | '' (全部)
})
const emit = defineEmits(['update:visible', 'update:modelValue', 'confirm'])

const categoryTree = ref([])
const currentCategoryId = ref(null)
const uploadCategoryId = ref(null)
const mediaList = ref([])
const loading = ref(false)
const searchKeyword = ref('')
const filterType = ref(props.accept || '')
const selectedIds = ref([])
const selectedItems = ref([])
const pagination = reactive({ current_page: 1, per_page: 28, total: 0, last_page: 1 })

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
const uploadAction = apiBaseUrl + '/manage/media/upload'
const uploadHeaders = { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }

const categoryDialogVisible = ref(false)
const categoryFormRef = ref(null)
const categorySubmitting = ref(false)
const categoryForm = reactive({ name: '', parent_id: null })
const categoryRules = { name: [{ required: true, message: '请输入目录名称', trigger: 'blur' }] }

const previewDialogVisible = ref(false)
const previewUrl = ref('')
const previewName = ref('')

const onImgError = (e) => { e.target.style.display = 'none' }

const colsPerRow = 7
const rows = computed(() => {
  const result = []
  for (let i = 0; i < mediaList.value.length; i += colsPerRow) {
    result.push(mediaList.value.slice(i, i + colsPerRow))
  }
  return result
})

const initData = async () => { await Promise.all([fetchCategoryTree(), fetchMedia()]) }

const fetchCategoryTree = async () => {
  try { const r = await mediaApi.getMediaCategoryTree(); categoryTree.value = r.data || [] } catch {}
}

// 弹窗从隐藏变为打开/从打开变为关闭：重置所有状态（分页/搜索/选中），避免上次选择残留
const resetPicker = () => {
  pagination.current_page = 1
  searchKeyword.value = ''
  filterType.value = props.accept || ''
  currentCategoryId.value = null
  uploadCategoryId.value = null
  selectedIds.value = []
  selectedItems.value = []
}

watch(() => props.visible, (val, oldVal) => {
  if (val !== oldVal) resetPicker()
})

const fetchMedia = async () => {
  loading.value = true
  try {
    const params = { page: pagination.current_page, per_page: pagination.per_page, type: props.accept || filterType.value || undefined, search: searchKeyword.value || undefined }
    if (currentCategoryId.value) params.category_id = currentCategoryId.value
    const r = await mediaApi.getMediaList(params)
    mediaList.value = r.data || []
    pagination.current_page = r.pagination?.current_page || 1
    pagination.total = r.pagination?.total || 0
    const ids = new Set(mediaList.value.map(i => i.id))
    selectedIds.value = selectedIds.value.filter(id => ids.has(id))
  } catch {} finally { loading.value = false }
}

const handleCategoryClick = (data, node) => {
  currentCategoryId.value = node?.key || data?.id || null
  pagination.current_page = 1
  fetchMedia()
}
const handleSearch = () => { pagination.current_page = 1; fetchMedia() }

const handleSelect = (item) => {
  if (props.mode === 'single') { selectedIds.value = [item.id]; selectedItems.value = [item]; return }
  const idx = selectedIds.value.indexOf(item.id)
  if (idx > -1) { selectedIds.value.splice(idx, 1); selectedItems.value.splice(idx, 1) }
  else {
    if (props.maxCount && selectedIds.value.length >= props.maxCount) { ElMessage.warning('最多' + props.maxCount + '个'); return }
    selectedIds.value.push(item.id); selectedItems.value.push(item)
  }
}

const confirmSelect = () => {
  if (props.mode === 'single') {
    emit('update:modelValue', selectedItems.value[0]?.url || null)
    emit('confirm', selectedItems.value[0] || null)
  } else {
    emit('update:modelValue', selectedItems.value.map(i => i.url))
    emit('confirm', selectedItems.value)
  }
  emit('update:visible', false)
}

const beforeUpload = (file) => {
  if (!uploadCategoryId.value) { ElMessage.warning('请选择目录'); return false }
  const ok = file.type.startsWith('image/') || file.type.startsWith('video/')
  if (!ok) { ElMessage.error('仅支持图片/视频'); return false }
  if (file.type.startsWith('image/') && file.size > 5*1024*1024) { ElMessage.error('图片≤5MB'); return false }
  if (file.type.startsWith('video/') && file.size > 100*1024*1024) { ElMessage.error('视频≤100MB'); return false }
  return true
}

const handleUploadSuccess = (res) => {
  if (res.code === 200 || res.errno === 0) { ElMessage.success('上传成功'); fetchMedia() }
  else { ElMessage.error(res.message || '失败') }
}

const handleCopyUrl = (url) => {
  navigator.clipboard?.writeText(url).then(() => ElMessage.success('已复制')).catch(() => ElMessage.success('已复制'))
}

const handlePreview = (item) => { previewUrl.value = item.url; previewName.value = item.name; previewDialogVisible.value = true }

const showAddCategoryDialog = () => { categoryForm.name = ''; categoryForm.parent_id = null; categoryDialogVisible.value = true }
const showAddChildDialog = (p) => { categoryForm.name = ''; categoryForm.parent_id = p.id; categoryDialogVisible.value = true }

const handleCreateCategory = async () => {
  const ok = await categoryFormRef.value?.validate().catch(() => false)
  if (!ok) return
  categorySubmitting.value = true
  try { await mediaApi.createMediaCategory({ ...categoryForm }); ElMessage.success('创建成功'); categoryDialogVisible.value = false; await fetchCategoryTree() }
  catch { ElMessage.error('失败') }
  finally { categorySubmitting.value = false }
}
</script>

<style scoped>
/* ====== 整体布局 ====== */
.media-picker { display:flex; gap:0; height:560px }

/* ====== 左侧目录 200px ====== */
.picker-sidebar {
  width:200px; flex-shrink:0;
  border-right:1px solid #e4e7ed; padding-right:10px;
  display:flex; flex-direction:column; overflow:hidden;
}
.sidebar-header {
  display:flex; justify-content:space-between; align-items:center;
  font-weight:600; font-size:13px; padding:4px 0 6px; flex-shrink:0;
}
.sidebar-footer { margin-top:auto; padding:6px 0 4px; border-top:1px solid #e4e7ed; flex-shrink:0; }
.sidebar-footer .el-button { width:100% }

/* 全部素材行（放在树上方） */
.all-media-item {
  display:flex; align-items:center; gap:2px;
  padding:3px 8px; margin:2px 0; border-radius:4px;
  cursor:pointer; font-size:12px; color:#606266;
  height:28px; box-sizing:border-box;
}
.all-media-item:hover { background:#f5f5f5; color:#333 }
.all-media-item.active { color:#9E8569; font-weight:600; background:#fdf8f0 }
.picker-category-node { display:flex; align-items:center; flex:1; min-width:0; }
.picker-category-node .node-label { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:12px }
.picker-category-node .el-button { opacity:0 }
.picker-category-node:hover .el-button { opacity:1 }

/* ====== 右侧主区域 ====== */
.picker-main { flex:1; display:flex; flex-direction:column; overflow:hidden; min-width:0; margin-left:12px }

/* ====== 工具栏（单行） ====== */
.toolbar {
  display:flex; align-items:center; justify-content:space-between;
  padding:0 0 8px; gap:10px; flex-shrink:0; min-height:32px;
}
.toolbar-left { display:flex; align-items:center; gap:6px }
.toolbar-right { display:flex; align-items:center; gap:6px }
.toolbar-count { font-size:12px; color:#909399; white-space:nowrap }

/* ====== 行分割素材网格 ====== */
.media-grid { flex:1; overflow-y:auto; padding:2px }
.media-row { display:grid; grid-template-columns:repeat(7, 1fr); gap:12px; margin-bottom:28px }
.media-row:last-child { margin-bottom:0 }

.media-item {
  position:relative;
  border:2px solid #e4e7ed; border-radius:4px;
  cursor:pointer; background:#f5f5f5;
  aspect-ratio:1/1; overflow:hidden;
  transition:border-color .15s, background .15s;
}
.media-item:hover { border-color:#9E8569 }
.media-item.selected { border-color:#9E8569; background:#fdf8f0 }

/* ====== 图片预览 ====== */
.media-preview { width:100%; height:100%; overflow:hidden }
.media-preview-img,
.media-preview video { width:100%; height:100%; object-fit:cover; display:block }
.video-preview { position:relative }
.video-badge {
  position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
  color:rgba(255,255,255,.9); pointer-events:none;
}

/* ====== 底部信息条（absolute 浮在卡片底部） ====== */
.media-bar {
  position:absolute; bottom:0; left:0; right:0;
  display:flex; align-items:center; gap:2px;
  padding:3px 6px;
  background:rgba(0,0,0,.5); backdrop-filter:blur(3px);
  height:26px; box-sizing:border-box;
}
.bar-name { flex:1; font-size:11px; color:#fff; overflow:hidden; text-overflow:ellipsis; white-space:nowrap }
.bar-btn {
  background:none; border:none; color:#fff; cursor:pointer;
  padding:2px; border-radius:2px; display:flex; opacity:.7; flex-shrink:0;
}
.bar-btn:hover { opacity:1; background:rgba(255,255,255,.15) }

/* ====== 选中标记 ====== */
.selected-mark {
  position:absolute; top:3px; right:3px;
  width:18px; height:18px; border-radius:50%;
  background:#9E8569; color:#fff;
  display:flex; align-items:center; justify-content:center;
}

/* ====== 空状态 ====== */
.empty-state { grid-column:1/-1; display:flex; justify-content:center; padding:40px 0 }

/* ====== 分页 ====== */
.pagination-bar { flex-shrink:0; display:flex; justify-content:flex-end; padding-top:8px }

/* ====== 大图预览 ====== */
.preview-image-wrap { display:flex; align-items:center; justify-content:center; min-height:150px }

/* ====== 底部操作 ====== */
.dlg-footer { display:flex; align-items:center; justify-content:space-between; width:100% }
.dlg-actions { display:flex; gap:8px }
.multi-count { font-size:12px; color:#909399 }
</style>
